import { useCallback, useState } from "react";
import { JobStatus } from "../../../../pages/playground/types";
import { bulkUpdateJobStatus } from "../../../../api/batches";

interface BulkUpdateResult {
  successful_updates: Array<{
    job_id: string;
    old_status: string;
    new_status: string;
    master_id: string;
  }>;
  failed_updates: Array<{
    job_id: string;
    error: string;
    reason: string;
  }>;
  summary: {
    total_requested: number;
    successful: number;
    failed: number;
  };
}

export interface UseJobBulkActionsResult {
  showBulkActions: boolean;
  selectedJobs: Set<string>;
  bulkLoading: boolean;
  bulkResults: BulkUpdateResult | null;
  bulkResultsDialogOpen: boolean;

  handleJobSelectionChange: (jobId: string, selected: boolean) => void;
  handleSelectAll: (selected: boolean, allJobIds: string[]) => void;
  handleClearSelection: () => void;
  handleBulkStatusChange: (newStatus: JobStatus) => Promise<void>;
  toggleBulkActions: () => void;
  closeBulkResultsDialog: () => void;
}

interface UseJobBulkActionsProps {
  onRefresh: () => Promise<void>;
}

export const useJobBulkActions = ({
  onRefresh,
}: UseJobBulkActionsProps): UseJobBulkActionsResult => {
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [selectedJobs, setSelectedJobs] = useState<Set<string>>(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkResults, setBulkResults] = useState<BulkUpdateResult | null>(null);
  const [bulkResultsDialogOpen, setBulkResultsDialogOpen] = useState(false);

  const handleJobSelectionChange = useCallback(
    (jobId: string, selected: boolean) => {
      setSelectedJobs((prev) => {
        const newSet = new Set(prev);
        if (selected) {
          newSet.add(jobId);
        } else {
          newSet.delete(jobId);
        }
        return newSet;
      });
    },
    []
  );

  const handleSelectAll = useCallback(
    (selected: boolean, allJobIds: string[]) => {
      if (selected) {
        setSelectedJobs(new Set(allJobIds));
      } else {
        setSelectedJobs(new Set());
      }
    },
    []
  );

  const handleClearSelection = useCallback(() => {
    setSelectedJobs(new Set());
  }, []);

  const handleBulkStatusChange = useCallback(
    async (newStatus: JobStatus) => {
      if (selectedJobs.size === 0) return;

      setBulkLoading(true);
      try {
        const response = await bulkUpdateJobStatus({
          job_ids: Array.from(selectedJobs),
          new_status: newStatus,
        });

        setBulkResults(response.data.data);
        setBulkResultsDialogOpen(true);
        setSelectedJobs(new Set());

        // Refresh job list
        await onRefresh();
      } catch (error) {
        console.error("Bulk update failed:", error);
        // You could show an error dialog here
      } finally {
        setBulkLoading(false);
      }
    },
    [selectedJobs, onRefresh]
  );

  const toggleBulkActions = useCallback(() => {
    setShowBulkActions((prev) => !prev);
    setSelectedJobs(new Set()); // Clear selection when toggling
  }, []);

  const closeBulkResultsDialog = useCallback(() => {
    setBulkResultsDialogOpen(false);
  }, []);

  return {
    // State
    showBulkActions,
    selectedJobs,
    bulkLoading,
    bulkResults,
    bulkResultsDialogOpen,

    // Actions
    handleJobSelectionChange,
    handleSelectAll,
    handleClearSelection,
    handleBulkStatusChange,
    toggleBulkActions,
    closeBulkResultsDialog,
  };
};
